import { z } from "zod";

const requiredText = (label: string) => z.string().trim().min(1, `${label} is required.`).max(150, "Use at most 150 characters.");

export const consultantAgencySchema = z.object({
  firstName: requiredText("First name"),
  lastName: requiredText("Last name"),
  preferredName: z.string().trim().max(150, "Use at most 150 characters."),
  email: z.string().trim().min(1, "Consultant email is required.").email("Enter a valid email address."),
  phone: requiredText("Consultant phone").refine(
    (value) => /^[+\d\s().-]+$/.test(value) && value.replace(/\D/g, "").length >= 7 && value.replace(/\D/g, "").length <= 15,
    "Enter a valid phone number, including country code if applicable.",
  ),
  role: requiredText("Role / Position"),
  clientProject: requiredText("Client / Project"),
  primaryContact: requiredText("Primary Caelius contact"),
});

export type AgencyConsultantValues = z.infer<typeof consultantAgencySchema>;
export const INITIAL_VALUES: AgencyConsultantValues = {
  firstName: "", lastName: "", preferredName: "", email: "", phone: "", role: "", clientProject: "", primaryContact: "",
};

export const FIELDS: { key: keyof AgencyConsultantValues; label: string; placeholder: string; required: boolean; type?: "email" | "tel" }[] = [
  { key: "firstName", label: "First Name", placeholder: "Enter First Name", required: true },
  { key: "lastName", label: "Last Name", placeholder: "Enter Last Name", required: true },
  { key: "preferredName", label: "Preferred Name", placeholder: "Enter Preferred Name", required: false },
  { key: "email", label: "Email", placeholder: "Enter Email", required: true, type: "email" },
  { key: "phone", label: "Phone Number", placeholder: "Enter Phone Number", required: true, type: "tel" },
  { key: "role", label: "Role / Position", placeholder: "Enter Role / Position", required: true },
  { key: "clientProject", label: "Client / Project", placeholder: "Enter Client / Project", required: true },
  { key: "primaryContact", label: "Primary Caelius Contact", placeholder: "Enter Primary Caelius Contact", required: true },
];

export const DOCUMENTS = [
  { id: "taxForm", label: "Completed and signed W-9 / applicable W-8", required: true, hint: "Provide the applicable W-8 for an international agency." },
  { id: "vendorAgreement", label: "Executed MSA / Vendor Agreement with Caelius", required: false, hint: "If applicable." },
  { id: "engagementAuthorization", label: "Executed SOW / PO or other engagement authorization", required: false, hint: "Attach the authorization for this engagement." },
  { id: "insurance", label: "Certificate of Insurance", required: false, hint: "If required." },
  { id: "businessLicense", label: "Business license", required: false, hint: "If applicable." },
  { id: "agencyResponsibility", label: "Agency responsibility agreement", required: false, hint: "Confirming responsibility for the consultant's compensation and employment/contractor obligations, as applicable." },
  { id: "screening", label: "Background-check / screening confirmation", required: false, hint: "If required by the engagement." },
  { id: "consultantProfile", label: "Consultant resume / profile and relevant credentials", required: false, hint: "Attach the consultant profile and credentials." },
  { id: "confidentiality", label: "Executed NDA / confidentiality documentation", required: false, hint: "If required." },
  { id: "clientCompliance", label: "Client-specific onboarding or compliance documentation", required: false, hint: "As requested by Caelius." },
] as const;

export type AgencyDocumentId = typeof DOCUMENTS[number]["id"];
export type AgencyDocuments = Partial<Record<AgencyDocumentId, File>>;
export interface AgencyOnboardingSubmission {
  consultant: AgencyConsultantValues;
  documents: AgencyDocuments;
}

export const STEPS = [
  { id: "consultant", label: "Consultant Information" },
  { id: "documents", label: "Required Agency Documentation" },
  { id: "review", label: "Review" },
];

// Temporary asset requested for the UI preview. Replace with the actual W-9 PDF.
export const W9_DOWNLOAD_URL = "/assets/W9_Form.pdf"; 

export function validateAgencyFile(file: File): string | undefined {
  if (!/\.(pdf|doc|docx|png|jpe?g|xlsx)$/i.test(file.name)) return "Choose a PDF, Word document, PNG, JPG, or Excel file.";
  if (file.size === 0) return "The selected file is empty.";
  if (file.size > 10 * 1024 * 1024) return "Choose a file up to 10 MB.";
  return undefined;
}
